import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useForm, Controller } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { portfolioService, CreateTransactionData } from '../../services/portfolioService';

interface TransactionDialogProps {
  open: boolean;
  onClose: () => void;
  portfolioId: number;
  onSuccess: () => void;
}

interface TransactionFormData extends Omit<CreateTransactionData, 'portfolio_id' | 'transaction_date'> {
  transaction_date: Dayjs | null;
}

const TransactionDialog: React.FC<TransactionDialogProps> = ({
  open,
  onClose,
  portfolioId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<TransactionFormData>({
    defaultValues: {
      stock_symbol: '',
      stock_name: '',
      transaction_type: 'BUY' as 'BUY' | 'SELL',
      quantity: 0,
      price: 0,
      transaction_date: dayjs(),
    },
  });

  const validateForm = (data: TransactionFormData): boolean => {
    clearErrors();
    let isValid = true;

    if (!data.stock_symbol.trim()) {
      setError('stock_symbol', { message: 'Stock symbol is required' });
      isValid = false;
    }
    if (!data.stock_name.trim()) {
      setError('stock_name', { message: 'Stock name is required' });
      isValid = false;
    }
    if (data.quantity <= 0) {
      setError('quantity', { message: 'Quantity must be positive' });
      isValid = false;
    }
    if (data.price <= 0) {
      setError('price', { message: 'Price must be positive' });
      isValid = false;
    }
    if (!data.transaction_date || !data.transaction_date.isValid()) {
      setError('transaction_date', { message: 'Transaction date is required' });
      isValid = false;
    }

    return isValid;
  };

  const onSubmit = async (data: TransactionFormData) => {
    if (!validateForm(data)) {
      return;
    }

    setLoading(true);
    try {
      await portfolioService.addTransaction({
        ...data,
        portfolio_id: portfolioId,
        transaction_date: data.transaction_date?.format('YYYY-MM-DD') || '',
      });
      toast.success('Transaction added successfully');
      reset();
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Add New Transaction</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stock Symbol"
                  {...register('stock_symbol')}
                  error={!!errors.stock_symbol}
                  helperText={errors.stock_symbol?.message}
                  placeholder="e.g., RELIANCE"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stock Name"
                  {...register('stock_name')}
                  error={!!errors.stock_name}
                  helperText={errors.stock_name?.message}
                  placeholder="e.g., Reliance Industries Ltd"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.transaction_type}>
                  <InputLabel>Transaction Type</InputLabel>
                  <Controller
                    name="transaction_type"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Transaction Type">
                        <MenuItem value="BUY">BUY</MenuItem>
                        <MenuItem value="SELL">SELL</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transaction_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Transaction Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.transaction_date,
                          helperText: errors.transaction_date?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  {...register('quantity', { valueAsNumber: true })}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Price per Share"
                  type="number"
                  {...register('price', { valueAsNumber: true })}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Add Transaction'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TransactionDialog; 